import { add, eachDayOfInterval, format, parseISO, startOfMonth, subDays } from 'date-fns';
import { UseFormSetValue } from 'react-hook-form/dist/types/form';
import {
  ConnectedFacility,
  type ProviderBookingValidate,
  ProviderRateTypeEnum,
  ShiftTypeEnum,
} from '../../../../api';
import { DATE_FORMAT } from '../../../constants/time';
import { ProviderScheduleWithKey } from '../Scheduler.types';
import { EPresetTime, FormOutput, IPresetMap, IShiftPreset, Shift } from '../ShiftPreset';

export const setToFormEndDateDueToSelectedTime = ({
  startTime,
  endTime,
  index,
  scheduleDate,
  form,
}): void => {
  const nextDay = add(scheduleDate, { days: 1 });

  if (startTime >= endTime) {
    form.setValue(`shifts.${index}.endOn`, format(nextDay, DATE_FORMAT));
  }

  if (startTime < endTime) {
    form.setValue(`shifts.${index}.endOn`, format(scheduleDate, DATE_FORMAT));
  }
  form.setValue(`shifts.${index}.isNew`, false);
};

export const getInitialTimes = (shift_type: ShiftTypeEnum) => {
  switch (true) {
    case shift_type === ShiftTypeEnum.DAYTIME:
      return extractShiftTinesFromPresetPeriod(EPresetTime.DAYSHIFT);
    case shift_type === ShiftTypeEnum.OVERNIGHT:
      return extractShiftTinesFromPresetPeriod(EPresetTime.NIGHTSHIFT);
    case shift_type === ShiftTypeEnum.ANYTIME:
      return extractShiftTinesFromPresetPeriod(EPresetTime.DAYSHIFT);
    default:
      return extractShiftTinesFromPresetPeriod(EPresetTime.DAYSHIFT);
  }
};

const PROVIDER_SHIFT_TYPE_MAP = {
  [ShiftTypeEnum.DAYTIME]: ProviderRateTypeEnum.DAY,
  [ShiftTypeEnum.OVERNIGHT]: ProviderRateTypeEnum.OVERNIGHT,
  [ShiftTypeEnum.ANYTIME]: ProviderRateTypeEnum.DAY,
};

// TODO: could cover with tests
export const getInitialShifts = (
  selectedDates: Date[],
  schedules: ProviderScheduleWithKey[],
  dateFormat: string = DATE_FORMAT
): Shift[] => {
  return selectedDates
    .reduce((acc, date): Shift[] => {
      const schedulesOnDate = schedules.filter(
        (schedule) => schedule.start_on === format(date, dateFormat)
      );

      if (schedulesOnDate.length > 0) {
        return [
          ...acc,
          ...schedulesOnDate.map(
            (schedule): Shift => ({
              startOn: format(date, dateFormat),
              endOn: format(date, dateFormat),
              ...getInitialTimes(schedule.shift_type),
              presetType: PROVIDER_SHIFT_TYPE_MAP[schedule.shift_type],
              isNew: false,
              providerRate: 0,
              id: 0,
              key: schedule.key,
              availability: schedule.shift_type,
            })
          ),
        ];
      } else {
        return [
          ...acc,
          {
            startOn: format(date, dateFormat),
            endOn: format(date, dateFormat),
            startTime: '--:--',
            endTime: '--:--',
            presetType: ProviderRateTypeEnum.DAY,
            providerRate: 0,
            id: 0,
            key: '0',
            isNew: false,
            availability: ShiftTypeEnum.DAYTIME,
          },
        ];
      }
    }, [])
    .map((shift, index) => ({ ...shift, id: index + 1 }));
};

export const extractShiftsFromPreset = (
  preset: IShiftPreset
): { startTime: string; endTime: string } => {
  const [startTime, endTime] = preset.timePeriod
    .split('_')[1] // DAYSHIFT_07:00-19:00 => 07:00-19:00
    .split('-'); // 07:00-19:00 => ['07:00', '19:00']

  return {
    startTime,
    endTime,
  };
};

export const extractShiftTinesFromPresetPeriod = (period: EPresetTime) => {
  const [startTime, endTime] = period.split('_')[1].split('-');
  return {
    startTime,
    endTime,
  };
};

// techdebt: Shim.  Add new interface for facility rate field mapping to map names used previously
// on ProviderLimited that were used to show/hide appropriate presets.
interface IFacilityRateFieldMap {
  [key: string]: string;
}

// Add mapping object for facility rate fields from naming used on ProviderLimited payload
const FACILITY_RATE_FIELD_MAP: IFacilityRateFieldMap = {
  facility_rate_day: 'hourly_day_rate',
  facility_rate_night: 'hourly_night_rate',
  facility_rate_24h: 'hourly_24h_rate',
  facility_rate_call: 'hourly_call_rate',
};

export const getAvailablePresets = (
  connectedFacility: ConnectedFacility | null,
  presetMap: IPresetMap,
  validatedShifts: ProviderBookingValidate[]
): IShiftPreset[] => {
  if (!connectedFacility?.facility) {
    return [];
  }

  return Object.values(presetMap)
    .filter((preset) => {
      // Find the facility field name that maps to this preset's provider field name
      const facilityFieldName = Object.entries(FACILITY_RATE_FIELD_MAP).find(
        ([_, hourlyName]) => hourlyName === preset.providerPresetFieldName
      )?.[0]; // Get the facility_rate_* key

      return facilityFieldName && connectedFacility[facilityFieldName];
    })
    .map((preset) => {
      const targetShifts = validatedShifts.filter(
        (shift) => shift.provider_rate_type === preset.type
      );
      const intermediate = targetShifts.length > 0 && targetShifts.length < validatedShifts.length;
      return {
        ...preset,
        intermediate,
        active: validatedShifts.every((shift) => shift.provider_rate_type === preset.type),
        partlyActive: validatedShifts.some((shift) => shift.provider_rate_type === preset.type),
        count: targetShifts.length,
        intermediateCount: intermediate ? validatedShifts.length : 0,
      };
    });
};

export const updatePreset = ({
  setValue,
  preset,
  shiftField,
  index,
}: {
  setValue: UseFormSetValue<FormOutput>;
  preset: IShiftPreset;
  shiftField: Shift;
  index: number;
}) => {
  if (preset.type === ProviderRateTypeEnum.CALL) {
    setValue(`shifts.${index}.presetType`, preset.type);
    setValue(`shifts.${index}.isNew`, false);
    return;
  }

  const { startTime, endTime } = extractShiftsFromPreset(preset);
  const date = parseISO(shiftField.startOn);

  setValue(`shifts.${index}.startTime`, startTime);
  setValue(`shifts.${index}.endTime`, endTime);
  setValue(`shifts.${index}.presetType`, preset.type);
  setValue(`shifts.${index}.isNew`, false);

  if ([ProviderRateTypeEnum.DAY, ProviderRateTypeEnum.CALL].includes(preset.type)) {
    setValue(`shifts.${index}.endOn`, format(date, DATE_FORMAT));
  } else {
    const nextDay = add(date, { days: 1 });
    setValue(`shifts.${index}.endOn`, format(nextDay, DATE_FORMAT));
  }
};

export const updateShiftByPreset = (preset: IShiftPreset, shiftField: Shift) => {
  if (preset.type === ProviderRateTypeEnum.CALL) {
    return {
      ...shiftField,
      presetType: preset.type,
      isNew: false,
    };
  }
  const { startTime, endTime } = extractShiftsFromPreset(preset);
  const date = parseISO(shiftField.startOn);
  return {
    ...shiftField,
    isNew: false,
    startTime,
    endTime,
    presetType: preset.type,
    endOn:
      preset.type === ProviderRateTypeEnum.DAY
        ? format(date, DATE_FORMAT)
        : format(add(date, { days: 1 }), DATE_FORMAT),
  };
};

export const getDatesUntilYesterday = (): Date[] => {
  const today = new Date();
  const startOfThisMonth = startOfMonth(today);
  const yesterday = subDays(today, 1);
  const start =
    format(today, DATE_FORMAT) === format(startOfThisMonth, DATE_FORMAT)
      ? startOfMonth(yesterday)
      : startOfThisMonth;

  return eachDayOfInterval({
    start,
    end: yesterday,
  });
};
